import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import moment from 'moment';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { Observable, Subscription } from 'rxjs';

import { Dietitian } from '../../../../data/dietitian/domain/dietitian';
import {
  Conversation,
  ConversationProps,
} from '../../../../data/message/domain/conversation/conversation';
import { Profile } from '../../../../data/message/domain/profile/profile';
import { Patient } from '../../../../data/patient/domain/patient';
import * as confirmationModalComponent from '../../confirmation-modal/confirmation-modal.component';
import { MessageModel } from '../message.models';
import { AudioRecordingService } from './audio-recording.service';
import {
  ConversationFacade,
  MessageData,
  MessageViewModel,
} from './conversation.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-conversation',
  templateUrl: './conversation.component.html',
  styleUrls: ['./conversation.component.scss'],
  providers: [ConversationFacade],
})
export class ConversationComponent implements OnDestroy, OnChanges {
  @ViewChild('messagesList') private messagesList: ElementRef;

  @Input() conversationId: string;
  @Input() isBack: boolean;
  @Input() isForward: boolean;
  @Input() origin = 'list';
  @Output() messageBack = new EventEmitter();
  @Output() messageForward = new EventEmitter();
  @Output() messageList = new EventEmitter();

  public conversation?: Conversation;
  public loading = false;
  public sending = false;
  public closingConversation = false;
  public bsModalRef?: BsModalRef;
  public messages: MessageModel[];
  public dayMessages: Map<string, MessageModel[]>;
  public audioUrl?: string;
  public audioBlob?: Blob;
  public imageUrl?: string;
  public messageContent?: string;
  private disableScrollDown = false;
  private messageSub: Subscription;
  private viewModel$: Observable<MessageViewModel>;
  private viewModelSub: Subscription;
  private patient: Patient;
  public dietitian: Dietitian;

  constructor(
    private modalService: BsModalService,
    private facade: ConversationFacade,
    public audioRecordingService: AudioRecordingService,
  ) {
    this.viewModel$ = this.facade.viewModel$;
  }

  ngOnChanges(): void {
    if (this.viewModelSub) {
      this.loadConversation();
      return;
    }
    this.viewModelSub = this.viewModel$.subscribe(async (state) => {
      if (!state.isLoading && state.dietitian) {
        this.dietitian = state.dietitian;

        this.loadConversation();
      }
    });
  }

  ngOnDestroy(): void {
    this.messageSub?.unsubscribe();
    this.viewModelSub?.unsubscribe();
  }

  async loadConversation() {
    try {
      const conversation = await this.facade.loadConversation(
        this.conversationId,
      );
      this.conversation = conversation;

      if (this.conversation && this.conversation.patient) {
        this.patient = await this.facade.loadPatient(
          this.conversation.patient.profileId?.id?.toString(),
        );
      }

      this.loadMessages();
    } catch {
      this.conversation = undefined;
      this.messages = [];
    }
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  onMessagesScroll() {
    const element = this.messagesList.nativeElement;
    const atBottom =
      element.scrollHeight - element.scrollTop === element.clientHeight;
    this.disableScrollDown = !(this.disableScrollDown && atBottom);
  }

  private scrollToBottom(): void {
    if (this.disableScrollDown) {
      return;
    }
    try {
      this.messagesList.nativeElement.scrollTop =
        this.messagesList.nativeElement.scrollHeight;
    } catch (err) {
      // nothing
    }
  }

  private groupConversationsByDay() {
    this.dayMessages = new Map<string, MessageModel[]>();
    for (let index = 0; index < this.messages.length; index++) {
      const conversation = this.messages[index];
      const formattedDate = moment(conversation.date)
        .startOf('day')
        .format('YYYY-MM-DD');

      if (!this.dayMessages.has(formattedDate)) {
        this.dayMessages.set(formattedDate, []);
      }

      this.dayMessages.get(formattedDate)?.push(conversation);
    }
  }

  loadMessages() {
    if (!this.conversation || !this.patient || !this.dietitian) {
      return;
    }

    this.loading = true;

    // Unsubscribe if we a subscription already exists
    this.messageSub?.unsubscribe();

    this.messageSub = this.facade
      .listenMessages(
        this.conversation.conversationId.id.toString(),
        this.dietitian,
        this.patient,
      )
      .subscribe((messages) => {
        this.loading = false;
        if (messages != this.messages && messages.length > 0) {
          // TODO: trigger event when new message was detected
          // this.facade.conversationChanged(conversations[conversations.length - 1]);
        }

        this.messages = messages;
        this.groupConversationsByDay();

        this.disableScrollDown = false;
        this.scrollToBottom();
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async pictureSelected(event: any): Promise<void> {
    if (!event.target.files?.length) {
      return undefined;
    }
    this.removeAudio();

    const file = event?.target?.files[0];
    event.target.value = '';

    const reader = new FileReader();
    reader.onload = () => (this.imageUrl = reader.result as string);
    reader.readAsDataURL(file);
    this.scrollToBottom();

    // return this.facade.updatePicture(file);
  }

  get isContentNotEmpty() {
    return (
      (this.messageContent && this.messageContent.length > 0) ||
      this.audioUrl ||
      this.imageUrl
    );
  }

  get isAttachment() {
    return this.imageUrl || this.audioUrl;
  }

  removeImage() {
    this.imageUrl = undefined;
  }

  removeAudio() {
    this.audioUrl = undefined;
    this.audioBlob = undefined;
  }

  removeText() {
    this.messageContent = undefined;
  }

  recordAudio() {
    this.audioRecordingService.startTimer().then(() => {
      this.stopAudioRecord();
    });
    this.removeAudio();
    this.removeImage();
    this.audioRecordingService.startRecording();
  }

  async stopAudioRecord() {
    const blob = await this.audioRecordingService.stopRecording();
    this.audioUrl = URL.createObjectURL(blob);
    this.audioBlob = blob;
    this.scrollToBottom();
  }

  convertDate(date: string) {
    return moment(date, 'YYYY-MM-DD').toDate();
  }

  async send() {
    if (!this.conversation || !this.dietitian || !this.conversation.patient) {
      return;
    }
    this.sending = true;

    const messageData: MessageData = {
      conversationId: this.conversation.conversationId.id,
      recipientId: this.conversation.patient.profileId.id,
      userId: this.dietitian.userId.id,
      senderId: this.dietitian.dietitianId.id,
      conversationProps: {
        ...this.conversation.props,
        dietitian: Profile.create(
          {
            ...this.conversation.dietitian.props,
            hasNewMessage: false,
            lastMessageReadDate: new Date(),
          },
          this.conversation.dietitian.profileId.id,
        ),
        patient: Profile.create(
          {
            ...this.conversation.patient.props,
            hasNewMessage: true,
          },
          this.conversation.patient.profileId.id,
        ),
        updatedAt: new Date(),
      } as ConversationProps,
    };

    if (this.imageUrl) {
      await this.facade.sendPicture(messageData, this.imageUrl);
      this.removeImage();
    } else if (this.audioBlob) {
      await this.facade.sendAudio(messageData, this.audioBlob);
      this.removeAudio();
    } else if (this.messageContent) {
      await this.facade.sendMessage(messageData, this.messageContent);
      this.removeText();
    }

    this.sending = false;
  }

  onCloseConversation() {
    const initialState: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        title: 'Marquer comme lu ?',
        subtitle: 'Voulez-vous vraiment marquer la conversation comme lu ?',
      },
    };
    this.bsModalRef = this.modalService.show(
      confirmationModalComponent.ConfirmationModalComponent,
      initialState,
    );
    if (
      this.bsModalRef.content instanceof
      confirmationModalComponent.ConfirmationModalComponent
    ) {
      this.bsModalRef.content.validate.subscribe(() => {
        this.closeConversation();
      });
    }
  }

  async closeConversation() {
    if (!this.conversation || !this.conversation.patient) {
      return;
    }

    this.closingConversation = true;
    this.conversation = await this.facade.saveConversation(
      {
        ...this.conversation.props,
        dietitian: Profile.create(
          {
            ...this.conversation.dietitian.props,
            hasNewMessage: false,
            lastMessageReadDate: new Date(),
          },
          this.conversation.dietitian.id,
        ),
        patient: Profile.create(
          {
            ...this.conversation.patient.props,
            hasNewMessage: false,
          },
          this.conversation.patient.id,
        ),
      } as ConversationProps,
      this.conversation.conversationId.id.toString(),
      true,
      true,
    );
    this.closingConversation = false;
  }
}
